import { createElement, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import { useDialog } from '../../components/Dialog.tsx';
import { useToast } from '../../components/Toast.tsx';
import { ApiError } from '../../lib/api.ts';
import { useCreateDocument, useDocumentTemplates } from '../../lib/document-queries.ts';
import { SavePicker } from '../sheets/SavePicker.tsx';
import { settleName } from '../drive/settleName.ts';

/**
 * Making a document, without the button around it.
 *
 * The companion to useSheetCreation, and here for the same reason: the Drive's
 * New menu and the dashboard's button should be doing one thing, not two
 * similar ones.
 */

export interface DocumentCreation {
  templates: ReturnType<typeof useDocumentTemplates>;
  createFrom(template: { id: string; name: string }): void;
}

export function useDocumentCreation(folderId: string | null): DocumentCreation {
  const navigate = useNavigate();
  const dialog = useDialog();
  const toast = useToast();
  const templates = useDocumentTemplates();
  const create = useCreateDocument();

  const createFrom = useCallback(
    (template: { id: string; name: string }) => {
      dialog.open((close) =>
        createElement(SavePicker, {
          title: 'Where should it go?',
          initialName: template.id === 'blank' ? 'Untitled document' : template.name,
          initialFolderId: folderId,
          confirmLabel: 'Create',
          onClose: close,
          onConfirm: (target: { name: string; folderId: string | null }) => {
            void (async () => {
              try {
                /*
                 * The name is settled before anything is created, so a name
                 * already in the folder is a question rather than a refusal.
                 * Null is the user answering "don't".
                 */
                const settled = await settleName(dialog, 'document', target.name, target.folderId);
                if (!settled) return;

                const created = await create.mutateAsync({
                  name: target.name,
                  folderId: target.folderId,
                  onConflict: settled.policy,
                  ...(template.id === 'blank' ? {} : { template: template.id }),
                });
                toast.success(`Created "${created.name}"`);
                navigate(`/documents/${created.id}`);
              } catch (caught) {
                toast.error(
                  'Could not create the document',
                  caught instanceof ApiError ? caught.message : 'Please try again.',
                );
              }
            })();
          },
        }),
      );
    },
    [dialog, folderId, create, toast, navigate],
  );

  return { templates, createFrom };
}
